<template>
  <BasePage :back-button="true" :pageTitle=getPageTitle show-menu>
    <!-- Loading State (including PWA background recovery) -->
    <LoadingSpinner v-if="loading || isRecovering"/>

    <!-- Main Content -->
    <div v-else-if="isReady" class="d-flex flex-column overflow-auto" :style="{height: `calc(100vh - ${showReadyToDepart ? 300 : 250}px)`}">

      <!-- Mode Toggle -->
      <SegmentedControl
          :options="modeOptions"
          v-model="viewMode"
          class="mb-3"
      />

      <!-- TL-09: opt-in live location for the ETA while the coach is on the road -->
      <LiveLocationShare
          v-if="hasDeparted && !hasArrived && effectiveWeekId"
          :week-id="effectiveWeekId"
          :transfer-ref="transferId.toString()"
          :direction="direction.toString()"
      />

      <!-- Loading Mode -->
      <TransferLoadingMode
          v-if="viewMode === 'loading'"
          :ticket-data="transferTicketData"
          :remaining-passengers="remainingPassengers"
          :loaded-passengers="loadedPassengers"
          :remaining-equipment="remainingEquipment"
          :loaded-equipment="loadedEquipment"
          @open-passenger-quick-view="openPassengerQuickView"
      />

      <!-- Details Mode -->
      <TransferDetailsMode
          v-else
          :direction="direction.toString()"
          :week-data="weekData"
          :transfer-data="effectiveTransferData"
          :transfer-id="transferId.toString()"
          :reps="reps"
          :passengers="passengers"
          :remaining-passengers="remainingPassengers"
          :loaded-passengers="loadedPassengers"
          :drop-off-passengers="dropOffPassengers"
          :ticket-data="transferTicketData"
          :additional-pax="additionalPax"
          :has-no-relevant-data="hasNoRelevantData"
          :is-ota-transfer="isOtaTransfer"
          :remaining-equipment="remainingEquipment"
          :loaded-equipment="loadedEquipment"
          @open-passenger-quick-view="openPassengerQuickView"
          @open-rep-quick-view="openRepQuickView"
      />

      <!-- Scanner Component -->
      <QrScannerModal
          v-if="showingScanner"
          :model-value="showingScanner"
          @close="resetState"
          @code-scanned="(code, scan) => loadPassenger('scan', code, scan)"
          @invalid-code="handleInvalidCode"
      />

      <!-- Name Search Modal -->
      <NameSearchModal
          :transferId="transferId"
          :is-open="showingNameSearch"
          :tickets="transferTicketData?.tickets || []"
          :allTickets="allTicketData?.tickets || []"
          :showAllTicketsTab="!isOtaTransfer"
          :heading="`Find ${__DICT__.passenger}`"
          @close="closeNameSearch"
          @select="loadPassenger('manual', $event)"
          @loadGroup="loadGroup"
          showTransferStatus
          liftPassStatusText="Loaded?"
          :isOtaTransfer="isOtaTransfer"
          :groups="externalPassengers?.groups || []"
          :externalTickets="externalPassengers?.tickets || []"
      />

    </div>
    <!-- Action Buttons: 1) scan barcode, 2) load by name, 3) ready / request to depart, 4) slide to depart -->
    <div class="fixed-bottom fixed-bottom-bar p-3">
      <!-- Initial State Buttons -->
      <template v-if="currentState === 'initial' && !showingScanner">
        <button class="btn btn-primary w-100 mb-2" @click="startScanner">
          {{ __DICT__.scanBarcode }}
        </button>
        <button class="btn btn-secondary w-100 mb-2" @click="startNameSearch">
          {{ __DICT__.loadByName }}
        </button>
      </template>

      <!-- Scanner Active State Button -->
      <template v-if="showingScanner">
        <button class="btn btn-secondary w-100 mb-2" @click="resetState">
          {{ __DICT__.cancel }}
        </button>
      </template>

      <!-- Ready to depart (leads) / Request to depart (general reps): sets the run to
           ready_to_depart, which notifies the OB team through the staff notifications -->
      <button
          v-if="showReadyToDepart"
          class="btn w-100 mb-2"
          :class="departRequested ? 'btn-outline-purple' : 'btn-purple'"
          :disabled="readyToDepartDisabled"
          data-test="ready-to-depart"
          @click="requestDeparture"
      >
        <IconCheck v-if="departRequested" class="icon me-1"/>
        {{ readyToDepartLabel }}
      </button>

      <SlideConfirmButton
          :title="__DICT__.loadTransferView.slideToDeparture"
          @complete="confirmDeparture"
          class="mb-2"
          v-if="!hasDeparted"
      >
        <template v-if="departRequested" #status>
          <TransferStatusBadge :status="runStatus"/>
        </template>
      </SlideConfirmButton>
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
      <div v-if="hasDeparted" class="alert alert-action">
        {{ __DICT__.loadTransferView.departedAt }}: {{ departedTime.replace('+0000 (Coordinated Universal Time)', '') }}
      </div>
      <SlideConfirmButton
          :title="__DICT__.loadTransferView.slideToArrival"
          @complete="confirmArrival"
          class="mb-2"
          v-if="hasDeparted && !hasArrived"
      />
      <div v-if="hasArrived" class="alert alert-action">
        {{ __DICT__.loadTransferView.arrivedAt }}: {{ arrivedTime.replace('+0000 (Coordinated Universal Time)', '') }}
      </div>
    </div>
    <!-- Success Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showSuccessModal }" tabindex="-1" style="display: block;"
         v-if="showSuccessModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-success">{{ __DICT__.success }}</div>
            <div>{{ groupLoadMessage || __DICT__.loadTransferView.passengerLoaded }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success w-100" @click="acknowledgeSuccess">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;"
         v-if="showErrorModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-danger">{{ __DICT__.error }}</div>
            <div>{{ error?.message || 'An error occurred' }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showSuccessModal || showErrorModal }"
         v-if="showSuccessModal || showErrorModal"></div>
    <AlertModal :message="modalMessage" :show="showModal" :showConfirm="showOK"
                :title="`Load Additional ${__DICT__.passenger}`"
                :type="modalType" @close="closeModal" @confirm="confirmModal">
    </AlertModal>

    <AlertModal :message="__DICT__.loadTransferView.confirmDepartureMessage"
                :show="showConfirmDeparture" title="Departure"
                type="info" @close="showConfirmDeparture = false" @confirm="departureConfirmed">
    </AlertModal>

    <AlertModal :message="__DICT__.loadTransferView.confirmArrivalMessage"
                :show="showConfirmArrival" title="Arrival"
                type="info" @close="showConfirmArrival = false" @confirm="arrivalConfirmed">
    </AlertModal>

    <!-- Passenger Quick View Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showPassengerModal }" tabindex="-1" style="display: block;"
         v-if="showPassengerModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ __DICT__.passenger }} {{ __DICT__.details }} - {{ transferId }}</h5>
            <button type="button" class="btn-close" @click="closePassengerModal"></button>
          </div>
          <div class="modal-body" v-if="selectedPassengerData">
            <table class="table table-vcenter">
              <tbody>
              <tr>
                <th>{{ __DICT__.name }}</th>
                <td>{{ selectedPassengerData.name }}</td>
              </tr>
              <tr v-if="selectedPassengerData.external">
                <th></th>
                <td><div class="badge badge-pill bg-orange text-white">{{ __DICT__.loadTransferView.externalPassenger }}</div></td>
              </tr>
              <tr>
                <th>{{ __DICT__.bookingRefShort }}</th>
                <td>{{ selectedPassengerData.bookingRef }}</td>
              </tr>
              <tr v-if="selectedPassengerData.groupName">
                <th>{{ __DICT__.group }}</th>
                <td>{{ selectedPassengerData.groupName}}</td>
              </tr>
              <tr>
                <th>{{ __DICT__.phone }}</th>
                <td>
                  <a v-if="selectedPassengerData.phone"
                     class="link-azure"
                     @click="handlePhoneClick(selectedPassengerData.phone)">
                    {{ selectedPassengerData.phone }}
                  </a>
                  <span v-else class="text-muted">{{ __DICT__.notAvailable }}</span>
                </td>
              </tr>
              <tr>
                <th>{{ __DICT__.status }}</th>
                <td>
                    <span class="badge text-white"
                          :class="selectedPassengerData.loaded === 1 ? 'bg-success' : 'bg-danger'">
                      {{ selectedPassengerData.loaded === 1 ? __DICT__.loaded : __DICT__.notLoaded }}
                    </span>
                </td>
              </tr>
              <tr>
                <th>{{ __DICT__.pickUp }}</th>
                <td>{{ selectedPassengerData.loadPoint }}</td>
              </tr>
              <tr>
                <th>{{ __DICT__.dropOff }}</th>
                <td>{{ selectedPassengerData.dropOffPoint }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <template v-if="selectedPassengerData?.isSameWeek">
              <button type="button" class="btn btn-primary" @click="goToPassengerProfile">
                {{ __DICT__.viewFullProfile }}
              </button>
            </template>
            <template v-else>
              <div class="text-muted small">
                {{ __DICT__.loadTransferView.passengerDifferentWeekBefore }} {{ __DICT__.passenger }} {{ __DICT__.loadTransferView.passengerDifferentWeekAfter }}
              </div>
            </template>
            <button type="button" class="btn btn-secondary" @click="closePassengerModal">
              {{ __DICT__.close }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showPassengerModal" @click="closePassengerModal"></div>

    <!-- Rep Quick View Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showRepModal }" tabindex="-1" style="display: block;"
         v-if="showRepModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ __DICT__.repDetails }} - {{ transferId }}</h5>
            <button type="button" class="btn-close" @click="closeRepModal"></button>
          </div>
          <div class="modal-body" v-if="selectedRepData">
            <table class="table table-vcenter">
              <tbody>
              <tr>
                <th>{{ __DICT__.name }}</th>
                <td>{{ selectedRepData.name }}</td>
              </tr>
              <tr v-if="selectedRepData.phone">
                <th>{{ __DICT__.phone }}</th>
                <td>
                  <a class="link-azure" @click="handlePhoneClick(selectedRepData.phone)">
                    {{ selectedRepData.phone }}
                  </a>
                </td>
              </tr>
              <tr v-if="selectedRepData.resort">
                <th>{{ __DICT__.resort }}</th>
                <td>{{ selectedRepData.resort }}</td>
              </tr>
              <tr v-if="selectedRepData.pickup">
                <th>{{ __DICT__.pickUp }}</th>
                <td>{{ selectedRepData.pickup }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <template v-if="selectedRepData?.isInCurrentWeek">
              <button type="button" class="btn btn-primary" @click="goToRepProfile">
                {{ __DICT__.viewFullProfile }}
              </button>
            </template>
            <template v-else>
              <div class="text-muted small">
                {{ __DICT__.repDifferentWeek }}
              </div>
            </template>
            <button type="button" class="btn btn-secondary" @click="closeRepModal">
              {{ __DICT__.close }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showRepModal" @click="closeRepModal"></div>

    <!-- Phone Action Sheet -->
    <PhoneNumberActionSheet
        :phone-number="selectedPhoneNumber"
        :is-visible="showPhoneActionSheet"
        @close="showPhoneActionSheet = false"
    />

  </BasePage>
</template>

<script setup>
import {useWeekStore} from "@/stores/week.js";
import BasePage from "@/components/BasePage.vue";
import {useRoute, useRouter} from "vue-router";
import {ref, watch, onMounted, computed, onUnmounted} from "vue";
import {rememberTicketBookings} from '@/services/qrKeyCache.js';
import {api} from "@/services/api.js";
import {encodeBase64Url} from "@/services/base64.js";
import NameSearchModal from "@/components/NameSearchModal.vue";
import {getLocation} from '@/services/location.js';
import QrScannerModal from "@/components/QrScannerModal.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";
import {LoadingSpinner, SegmentedControl} from "@/components/atomic";
import SlideConfirmButton from "@/components/SlideConfirmButton.vue";
import AlertModal from "@/components/AlertModal.vue";
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import PhoneNumberActionSheet from "@/components/PhoneNumberActionSheet.vue";
import {passengerIndex} from "@/services/PassengerIndex.js";
import TransferLoadingMode from "@/components/TransferLoadingMode.vue";
import TransferDetailsMode from "@/components/TransferDetailsMode.vue";
import {useHaptics} from "@/composables/useHaptics.js";
import {useScanRedeem} from "@/composables/useScanRedeem.js";
import {loadOtaGroup} from "@/services/groupLoad.js";
import LiveLocationShare from "@/components/LiveLocationShare.vue";
import TransferStatusBadge from "@/components/TransferStatusBadge.vue";
import {IconCheck} from "@tabler/icons-vue";


// Route and Store
const route = useRoute();
const weekStore = useWeekStore();
const router = useRouter();

// Ensure week data is available (handles PWA background recovery)
const {isRecovering, isReady} = useEnsureWeekData();
const { successFeedback, errorFeedback } = useHaptics();

// View mode toggle
const viewMode = ref('loading');
const modeOptions = [
  { value: 'loading', label: 'Loading' },
  { value: 'details', label: 'Details' }
];

// Use weekId from query param if provided (for airport transfers), otherwise use store
const effectiveWeekId = computed(() => {
  return route.query.weekId || weekStore.systemId
})

// State Management
const loading = ref(true);
const transferId = ref(route.params.transferId);
const direction = ref(route.params.direction);
const transferTicketData = ref({});
// Set for a group load's success, which says how many were loaded.
const groupLoadMessage = ref(null);
const {processing, error, showSuccessModal, showErrorModal, redeem} = useScanRedeem({
  refresh: () => refreshData()
});
const entryMethod = ref(null);
const currentState = ref('initial');
const weekData = ref(null);
const scannedPassengers = ref([])
const transferData = ref(null)
const repData = ref([])
const allTicketData = ref([])
// The QR check can bind a scan to its ticket's booking (qr-binding-ticket-booking).
watch(() => allTicketData.value?.tickets, rememberTicketBookings);
const showModal = ref(false)
const modalMessage = ref("")
const modalType = ref("info")
let refreshTimeout = null;
const passengerToLoad = ref({})
const noTransferPurchasedFlag = ref(false)
const transferState = ref({})
const additionalPax = ref([])
const showOK = ref(true)
const showConfirmDeparture = ref(false)
const showConfirmArrival = ref(false)
const extraData = ref({})

// Passenger Quick View Modal state
const showPassengerModal = ref(false)
const selectedPassengerData = ref(null)
const showPhoneActionSheet = ref(false)
const selectedPhoneNumber = ref('')

// Rep Quick View Modal state
const showRepModal = ref(false)
const selectedRepData = ref(null)

const getPageTitle = computed(() =>
    `${transferId.value} |  ${direction.value.charAt(0).toUpperCase() + direction.value.slice(1)}`
)


const reps = computed(() => {
  // For external transfers, use extraData.reps; otherwise filter from store repData
  if (isExternalTransfer.value && extraData.value?.reps?.length > 0) {
    return extraData.value.reps
  }
  const repList = repData.value ?? []
  if (!Array.isArray(repList)) return []
  return repList.filter(rep => rep["OB Transport"] === route.params.transferId || rep["IB Transport"] === route.params.transferId)
})


const hasDeparted = computed(() => {
  if (!transferState.value || Object.keys(transferState.value).length === 0) return false
  if (direction.value === 'outbound') {
    return !!transferState.value?.departedOutbound
  }
  if (direction.value === 'inbound') {
    return !!transferState.value?.departedInbound
  }
  return false
})

const departedTime = computed(() => {
  if (!transferState.value || Object.keys(transferState.value).length === 0) return ''
  if (direction.value === 'outbound') {
    return transferState.value?.departedAtOutbound || ''
  }
  if (direction.value === 'inbound') {
    return transferState.value?.departedAtInbound || ''
  }
  return ''
})

const hasArrived = computed(() => {
  if (!transferState.value || Object.keys(transferState.value).length === 0) return false
  if (direction.value === 'outbound') {
    return !!transferState.value?.arrivedOutbound
  }
  if (direction.value === 'inbound') {
    return !!transferState.value?.arrivedInbound
  }
  return false
})

const arrivedTime = computed(() => {
  if (!transferState.value || Object.keys(transferState.value).length === 0) return ''
  if (direction.value === 'outbound') {
    return transferState.value?.arrivedAtOutbound || ''
  }
  if (direction.value === 'inbound') {
    return transferState.value?.arrivedAtInbound || ''
  }
  return ''
})

// Check if we have full week data available
const hasWeekData = computed(() => {
  return weekData.value && weekData.value.length > 0
})

// Check if viewing a transfer from a different week (external transfer)
const isExternalTransfer = computed(() => {
  // If we have a weekId in query params and it differs from the store's systemId
  return route.query.weekId && String(route.query.weekId) !== String(weekStore.systemId)
})

// Check if this is an OTA transfer (external passengers only)
const isOtaTransfer = computed(() => {
  return extraData.value?.dbTransfer?.ota === 1
})

// External passengers data (for OTA transfers)
const externalPassengers = ref({tickets: [], groups: [], total: 0, loaded: 0})

// Combined check: no relevant data for this transfer
const hasNoRelevantData = computed(() => {
  return isExternalTransfer.value || !hasWeekData.value
})

// Use extraData.transfer as fallback for capacity summary
const effectiveTransferData = computed(() => {
  return transferData.value || extraData.value?.transfer || null
})

const confirmDeparture = async () => {
  showConfirmDeparture.value = true;
}

// --- Ready / request to depart -------------------------------------------------
// The run (GET /api/transfer/run) gives the status, what the rep may set and
// whether they are a lead (admin, level 2+, or pax / loading area lead that day).
// Leads mark the coach "Ready to depart"; everyone else "Request to depart".
// Both set ready_to_depart, whose staff notification tells the OB team, who
// give permission to depart. The slide to depart is never blocked by it.
const runInfo = ref(null)
const requestingDepart = ref(false)
const loadTransferDict = (typeof __DICT__ !== 'undefined' && __DICT__.loadTransferView) || {}
const departLabels = {
  ready: loadTransferDict.readyToDepart || 'Ready to depart',
  request: loadTransferDict.requestToDepart || 'Request to depart',
  requested: loadTransferDict.departureRequested || 'Departure requested',
  failed: loadTransferDict.readyToDepartFailed || 'Could not mark the transfer ready to depart'
}
const DEPART_REQUESTED = ['ready_to_depart', 'permission_to_depart']

const runStatus = computed(() => runInfo.value?.run?.status || null)
const isLead = computed(() => !!runInfo.value?.isLead)
const departRequested = computed(() => DEPART_REQUESTED.includes(runStatus.value))
const showReadyToDepart = computed(() =>
    !!runInfo.value?.enabled && !hasDeparted.value && (runInfo.value?.canSetStatus || []).includes('ready_to_depart')
)
// The API refuses ready_to_depart until loading has started.
const readyToDepartDisabled = computed(() =>
    requestingDepart.value || departRequested.value || runStatus.value !== 'loading'
)
const readyToDepartLabel = computed(() => {
  if (departRequested.value && !isLead.value) return departLabels.requested
  return isLead.value ? departLabels.ready : departLabels.request
})

const loadRunInfo = async () => {
  if (!effectiveWeekId.value) {
    runInfo.value = null
    return
  }
  try {
    runInfo.value = await api.get(`/api/transfer/run/${effectiveWeekId.value}/${encodeURIComponent(transferId.value)}/${direction.value}`)
  } catch {
    // No run (or no access): the button simply does not show.
    runInfo.value = null
  }
}

const requestDeparture = async () => {
  if (readyToDepartDisabled.value) return
  requestingDepart.value = true
  try {
    await api.post('/api/transfer/status', {
      transferRef: transferId.value,
      weekId: effectiveWeekId.value,
      direction: direction.value,
      status: 'ready_to_depart'
    })
    await loadRunInfo()
    successFeedback()
  } catch (e) {
    error.value = {message: `${departLabels.failed}: ${e.message}`}
    showErrorModal.value = true
    errorFeedback()
  } finally {
    requestingDepart.value = false
  }
}

const departureConfirmed = async () => {
  if (!effectiveWeekId.value) {
    error.value = {message: 'Session data not available. Please go back and try again.'};
    showErrorModal.value = true;
    showConfirmDeparture.value = false;
    return false;
  }
  try {
    const {latitude, longitude} = await getLocation();
    await api.post(`/api/transfer/transferDeparted`, {
      transferId: transferId.value,
      direction: direction.value,
      weekId: effectiveWeekId.value,
      departureLat: latitude,
      departureLon: longitude
    })
    transferState.value = await api.get(`/api/transfer/transferState/${effectiveWeekId.value}/${transferId.value}`)
    loadRunInfo()
    showConfirmDeparture.value = false;
    return true;
  } catch (e) {
    console.error('Error confirming departure:', e);
    error.value = {message: 'Failed to confirm departure. Please try again.'};
    showErrorModal.value = true;
    showConfirmDeparture.value = false;
    return false;
  }
}

const confirmArrival = async () => {
  showConfirmArrival.value = true;
}

const arrivalConfirmed = async () => {
  if (!effectiveWeekId.value) {
    error.value = {message: 'Session data not available. Please go back and try again.'};
    showErrorModal.value = true;
    showConfirmArrival.value = false;
    return false;
  }
  try {
    const {latitude, longitude} = await getLocation();
    await api.post(`/api/transfer/transferArrived`, {
      transferId: transferId.value,
      direction: direction.value,
      weekId: effectiveWeekId.value,
      arrivalLat: latitude,
      arrivalLon: longitude
    })
    transferState.value = await api.get(`/api/transfer/transferState/${effectiveWeekId.value}/${transferId.value}`)
    showConfirmArrival.value = false;
    return true;
  } catch (e) {
    console.error('Error confirming arrival:', e);
    error.value = {message: 'Failed to confirm arrival. Please try again.'};
    showErrorModal.value = true;
    showConfirmArrival.value = false;
    return false;
  }
}

const confirmModal = async () => {
  showModal.value = false
  if (passengerToLoad.value.data) {
    await syncPassengerLoad(passengerToLoad.value.method, passengerToLoad.value.data, true, noTransferPurchasedFlag.value)
  } else {
    processing.value = false;
  }
  noTransferPurchasedFlag.value = false
}

const closeModal = () => {
  showModal.value = false;
  processing.value = false;
  passengerToLoad.value = {data: null, method: null}
  noTransferPurchasedFlag.value = false
}

// Computed Properties
const showingScanner = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'scan'
);

const showingNameSearch = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'manual'
);

const remainingEquipment = computed(() => {
  if (!remainingPassengers.value) return 0;

  return remainingPassengers.value.filter(passenger => passenger.equipment === true).length;
});

const loadedEquipment = computed(() => {
  if (!loadedPassengers.value) return 0;

  return loadedPassengers.value.filter(passenger => passenger.equipment === true).length;
});

const equipmentPercent = (p) => {
  let equipmentLoaded = p.loadedPassengers.filter(passenger => passenger.equipment === true).length;
  let equipmentNotLoaded = p.remainingPassengers.filter(passenger => passenger.equipment === true).length;
  return (equipmentLoaded / (equipmentLoaded + equipmentNotLoaded)) * 100
}
const equipmentTotal = (p) => {
  return p.remainingPassengers.filter(passenger => passenger.equipment === true).length + p.loadedPassengers.filter(passenger => passenger.equipment === true).length
}

const equipmentRaw = (p) => {
  return p.loadedPassengers.filter(passenger => passenger.equipment === true).length
}

const remainingPassengers = computed(() => {
  if (!transferTicketData.value?.tickets) return [];

  return transferTicketData.value.tickets
      .filter(ticket => ticket.loaded === 0 && ticket.deleted === 0)
      .map(ticket => {
        // Use indexed lookup for O(1) performance instead of O(n) array.find()
        const passenger = passengerIndex.getByReference(ticket.bookingRef);

        // Find the relevant transport place
        const place = passenger?.allocPlaces?.find(place =>
            place.transport?.transportType === "transfer" &&
            place.transport?.coach === transferId.value &&
            place.transport?.type === `transport-${direction.value}`
        );

        // Check equipment carriage
        const equipmentCarriage = place?.transport?.equipmentCarriage || '';
        const hasEquipment = equipmentCarriage.startsWith('I want');

        return {
          ...ticket,
          equipment: hasEquipment
        };
      });
});

const loadedPassengers = computed(() => {
  if (!transferTicketData.value?.tickets) return [];

  return transferTicketData.value.tickets
      .filter(ticket => ticket.loaded === 1)
      .map(ticket => {
        // Use indexed lookup for O(1) performance instead of O(n) array.find()
        const passenger = passengerIndex.getByReference(ticket.bookingRef);

        // Find the relevant transport place
        const place = passenger?.allocPlaces?.find(place =>
            place.transport?.transportType === "transfer" &&
            place.transport?.coach === transferId.value &&
            place.transport?.type === `transport-${direction.value}`
        );

        // Check equipment carriage
        const equipmentCarriage = place?.transport?.equipmentCarriage || '';
        const hasEquipment = equipmentCarriage.startsWith('I want');

        return {
          ...ticket,
          equipment: hasEquipment
        };
      });
});

const startLoadExtra = () => {
  const transfer = getPageTitle.value
  router.push(`/load-extras/${encodeBase64Url(transfer)}`)
}


const passengers = computed(() => {
  if (!transferTicketData.value?.tickets) return [];

  const passengersByLocation = transferTicketData.value.tickets
      .filter(ticket => ticket.deleted !== 1)
      .reduce((acc, ticket) => {
        const location = ticket.loadPoint;

        if (!location) return acc;

        if (!acc[location]) {
          acc[location] = {
            loadedPassengers: [],
            remainingPassengers: []
          };
        }

        // Create passenger object with properties matching template expectations
        const passengerData = {
          reference: ticket.bookingRef,
          passengerName: ticket.name,
          equipment: ticket.equipment || false
        };

        if (ticket.loaded === 1) {
          acc[location].loadedPassengers.push(passengerData);
        } else {
          acc[location].remainingPassengers.push(passengerData);
        }

        return acc;
      }, {});

  // Transform into final format
  return Object.entries(passengersByLocation).map(([location, {loadedPassengers, remainingPassengers}]) => ({
    location,
    loadedPassengers,
    remainingPassengers
  }));
});

// Group passengers by Drop Off location (mirrors pick up grouping)
const dropOffPassengers = computed(() => {
  if (!transferTicketData.value?.tickets) return [];

  const passengersByLocation = transferTicketData.value.tickets
      .filter(ticket => ticket.deleted !== 1)
      .reduce((acc, ticket) => {
        const location = ticket.dropOffPoint;

        if (!location) return acc;

        if (!acc[location]) {
          acc[location] = {
            loadedPassengers: [],
            remainingPassengers: []
          };
        }

        // Create passenger object with properties matching template expectations
        const passengerData = {
          reference: ticket.bookingRef,
          passengerName: ticket.name,
          equipment: ticket.equipment || false
        };

        if (ticket.loaded === 1) {
          acc[location].loadedPassengers.push(passengerData);
        } else {
          acc[location].remainingPassengers.push(passengerData);
        }

        return acc;
      }, {});

  // Transform into final format
  return Object.entries(passengersByLocation).map(([location, {loadedPassengers, remainingPassengers}]) => ({
    location,
    loadedPassengers,
    remainingPassengers
  }));
});

// Methods
const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

// The scan behind the passenger being loaded: { raw, token, verification }.
// Held across the confirm modal; null for name search. qrRequestCode/Fields
// ignore it when the barcode posted is not the scanned one (e.g. -NOTKT).
let currentScan = null

const loadPassenger = async (method, data, scan = null) => {
  if (processing.value) return;
  currentScan = method === 'scan' ? scan : null

  processing.value = true;
  error.value = null;
  showOK.value = true;

  // For OTA transfers, check transferTicketData (contains external passengers)
  // For regular transfers, check allTicketData
  let ticket = null;
  console.log(data)
  if (isOtaTransfer.value) {
    ticket = transferTicketData.value?.tickets?.find(t => t.barcode === data)
    if (ticket) {
      // External ticket found on this OTA transfer - load directly
      passengerToLoad.value = {data, method}
      await syncPassengerLoad(method, data)
      return
    }
  } else {
    ticket = allTicketData.value?.tickets?.find(t => t.barcode === data)
    if (ticket) {
      passengerToLoad.value = {data, method}
      if (ticket.transferId === transferId.value) {
        await syncPassengerLoad(method, data)
      } else if (ticket.transferId) {
        modalMessage.value = `This ${__DICT__.passenger} is not allocated to this transfer, please ensure you have space.`
        showModal.value = true;
        modalType.value = 'info'
      } else {
        noTransferPurchasedFlag.value = true
        modalMessage.value = `This ${__DICT__.passenger} has not purchased a transfer. Please confirm they are going to the correct resort. If they wish to travel on your transfer, they will be charged for it.`
        showModal.value = true;
        modalType.value = 'warning'
      }
      return
    } else {
      ticket = transferTicketData.value?.tickets?.find(t => t.barcode === data)
      if(ticket) {
        await syncPassengerLoad(method, data)
        return
      }
    }
  }

  // Ticket not found by barcode — try matching by bookingRef against weekData.
  // This handles QR scans where the code contains a bookingRef, not a transfer barcode.
  // QR barcode format is "BRAND-12345-ticketId", so bookingRef is the first two segments.
  const scannedRef = data.includes('-') ? `${data.split('-')[0]}-${data.split('-')[1]}` : data
  const weekPassenger = (weekData.value || []).find(p => p.reference === scannedRef || p.reference === data)

  if (weekPassenger) {
    // Passenger exists in this week but has no transfer ticket.
    // NOTKT suffix is a sentinel indicating no real ticket exists — the bookingRef column
    // in transferExtra is used to identify the passenger, not this synthetic barcode.
    const syntheticBarcode = `${weekPassenger.reference}-NOTKT`
    passengerToLoad.value = {data: syntheticBarcode, method}
    noTransferPurchasedFlag.value = true
    modalMessage.value = `This ${__DICT__.passenger} has not purchased a transfer. Please confirm they are going to the correct resort. If they wish to travel on your transfer, they will be charged for it.`
    showModal.value = true;
    modalType.value = 'warning'
    return
  }

  // Truly unknown passenger — not in this week at all
  processing.value = false
  passengerToLoad.value = {data: null, method: null}
  modalMessage.value = `This ${__DICT__.passenger} is not going to this resort, do NOT load.`
  showOK.value = false;
  showModal.value = true;
  modalType.value = 'danger'
}
const syncPassengerLoad = async (method, data, additional = false, noTransferPurchased = false) => {
  // Guard against missing session data (can happen when PWA resumes from background or viewing external transfer)
  if (!weekStore.repObj || !effectiveWeekId.value) {
    error.value = {message: 'Session data not available. Please go back and try again.'};
    showErrorModal.value = true;
    processing.value = false;
    return;
  }

  try {
    const {latitude, longitude} = await getLocation();
    const payload = {
      weekId: effectiveWeekId.value,
      barcode: qrRequestCode(currentScan, data),
      ...qrRequestFields(currentScan, data),
      handsetTimestamp: new Date().toISOString(),
      idempotentId: generateGuid(),
      repID: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      scanned: method === 'scan' ? 1 : 0,
      transferId: transferId.value,
      direction: direction.value,
      loadLat: latitude,
      loadLon: longitude,
      additional,
      noTransferPurchased
    };

    await redeem('/api/v3/scan/transfer', payload);
    resetState();
  } catch (e) {
    error.value = e;
    resetState();
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
    resetState();
  }
};

const startScanner = () => {
  entryMethod.value = 'scan';
};

const startNameSearch = () => {
  entryMethod.value = 'manual';
};

const closeNameSearch = () => {
  resetState();
};

// Load every OTA Extra in an OTA group in one action.
const loadGroup = async (groupId) => {
  if (processing.value) return;

  // Guard against missing session data
  if (!weekStore.repObj || !effectiveWeekId.value) {
    error.value = {message: 'Session data not available. Please go back and try again.'};
    showErrorModal.value = true;
    return;
  }

  processing.value = true;
  error.value = null;

  try {
    const {latitude, longitude} = await getLocation();
    const payload = {
      groupId,
      idempotentId: generateGuid(),
      weekId: effectiveWeekId.value,
      transferId: transferId.value,
      direction: direction.value,
      repID: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      handsetTimestamp: new Date().toISOString(),
      loadLat: latitude,
      loadLon: longitude
    };

    const result = await loadOtaGroup(payload);
    closeNameSearch();
    groupLoadMessage.value = result.message;
    showSuccessModal.value = true;
    successFeedback();
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
    errorFeedback();
    return;
  } finally {
    processing.value = false;
  }
  // A failed refresh after the load is not a failed load.
  try {
    await refreshData();
  } catch (e) {
    console.warn('Refresh after group load failed:', e);
  }
};

const resetState = () => {
  entryMethod.value = null;
  processing.value = false;
  currentState.value = 'initial';
};

const openRepQuickView = (rep) => {
  const userId = rep['GBS ID']
  const name = rep.Name

  // Check if this rep exists in the Pinia store (i.e., is in current week)
  const storeRep = (weekStore.repData || []).find(r => String(r['GBS ID']) === String(userId))

  selectedRepData.value = {
    name: name,
    userId: userId,
    phone: rep['Phone Number'] || null,
    resort: rep.Resort || null,
    pickup: rep['OB Pickup Point'] || rep['IB Pickup Point'] || null,
    isInCurrentWeek: !!storeRep
  }

  showRepModal.value = true
}

const closeRepModal = () => {
  showRepModal.value = false
  selectedRepData.value = null
}

const goToRepProfile = () => {
  if (selectedRepData.value?.userId) {
    router.push(`/rep/${selectedRepData.value.userId}`)
    closeRepModal()
  }
}

const acknowledgeSuccess = () => {
  showSuccessModal.value = false;
  groupLoadMessage.value = null;
};

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
};

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
};

const refreshData = async () => {
  // The run status (ready / request to depart) loads alongside; it never throws.
  const runInfoLoaded = loadRunInfo();
  // Fetch all data in parallel first, then update state once to prevent flickering
  const [
    regularTicketsResult,
    additionalPaxResult,
    extraDataResult,
    externalPassengersResult
  ] = await Promise.all([
    api.get(`/api/transfer/v2/allBarcodes/${transferId.value}/direction/${direction.value}`),
    api.get(`/api/transfer/additionalPax/${transferId.value}/${direction.value}`),
    api.get(`/api/airport/extraData/${transferId.value}`),
    api.get(`/api/transfer/v2/externalPassengers/${transferId.value}/direction/${direction.value}`)
        .catch(err => {
          console.warn('Failed to fetch external passengers:', err)
          return {tickets: [], groups: [], total: 0, loaded: 0}
        })
  ]);

  // Update state - determine which ticket data to use based on OTA flag
  const isOta = extraDataResult?.dbTransfer?.ota === 1;

  extraData.value = extraDataResult;
  additionalPax.value = additionalPaxResult;
  externalPassengers.value = externalPassengersResult;

  // For OTA transfers, use external passengers; otherwise use regular tickets
  //transferTicketData.value = isOta ? externalPassengersResult : regularTicketsResult;
  if (isOta) {
    transferTicketData.value = externalPassengersResult
  } else {
    transferTicketData.value = {
      total: regularTicketsResult.total + externalPassengersResult.total,
      loaded: regularTicketsResult.loaded + externalPassengersResult.loaded,
      tickets: [...regularTicketsResult.tickets, ...externalPassengersResult.tickets]
    }
  }


  // Week-specific data - only available if we have a valid weekId
  if (effectiveWeekId.value) {
    const [res, stateResult] = await Promise.all([
      api.get(`/api/transfer/allTickets/${effectiveWeekId.value}/${direction.value}`),
      api.get(`/api/transfer/transferState/${effectiveWeekId.value}/${transferId.value}`)
    ]);

    transferState.value = stateResult;

    const noTransferPax = []
    const resTickets = res?.tickets ?? []
    for (const p of (weekData.value || [])) {
      if (resTickets.find(r => r.bookingRef === p.reference)) {
        continue;
      }
      noTransferPax.push(
          {
            "transferTicketId": null,
            "bookingRef": p.reference,
            "name": p.passengerName,
            "barcode": `${p.reference}-NOTKT`,
          }
      )
    }
    allTicketData.value = {total: res?.total ?? 0, loaded: res?.loaded ?? 0, tickets: [...resTickets, ...noTransferPax]}
  } else {
    // For external transfers without week context, use transfer ticket data directly
    allTicketData.value = {
      total: transferTicketData.value?.tickets?.length || 0,
      loaded: transferTicketData.value?.loaded || 0,
      tickets: transferTicketData.value?.tickets || []
    }
    transferState.value = {}
  }
  await runInfoLoaded;
};

const refreshDataHook = () => {
  refreshData();
  refreshTimeout = setTimeout(refreshDataHook, 15000);
};

const openPassengerQuickView = (passenger) => {
  // Get the booking reference (different property names depending on source)
  const bookingRef = passenger.reference || passenger.bookingRef

  // Find the ticket data from transferTicketData - all modal data comes from here
  const ticket = transferTicketData.value?.tickets?.find(t => t.bookingRef === bookingRef)

  if (!ticket) {
    console.warn('Ticket not found for bookingRef:', bookingRef)
    return
  }

  selectedPassengerData.value = {
    bookingRef: ticket.bookingRef,
    name: ticket.name,
    phone: ticket.phone,
    groupName: ticket.groupName,
    external: !!ticket.createdBy,
    loaded: ticket.loaded,
    loadPoint: ticket.loadPoint,
    dropOffPoint: ticket.dropOffPoint,
    weekId: ticket.weekId,
    isSameWeek: (ticket.weekId === weekStore.systemId || !ticket.weekId) && !ticket.createdBy
  }

  showPassengerModal.value = true
}

const closePassengerModal = () => {
  showPassengerModal.value = false
  selectedPassengerData.value = null
}

const handlePhoneClick = (number) => {
  if (number) {
    selectedPhoneNumber.value = number
    showPhoneActionSheet.value = true
  }
}

const goToPassengerProfile = () => {
  if (selectedPassengerData.value?.bookingRef) {
    router.push(`/passenger/${selectedPassengerData.value.bookingRef}`)
    closePassengerModal()
  }
}

// Initial Data Load
onMounted(async () => {
  // Check if this is an external transfer (different week than what's in the store)
  const isExternal = route.query.weekId && String(route.query.weekId) !== String(weekStore.systemId)

  // Set store data - use empty arrays for external transfers since store data is for a different week
  repData.value = isExternal ? [] : (weekStore.repData || []);
  weekData.value = isExternal ? [] : (weekStore.weekData || []);

  // Use query weekId for airport transfers, otherwise use store weekId
  const weekIdForTransferData = route.query.weekId || weekStore.weekId

  try {
    // Load transfer data - may not find the transfer if viewing external transfer
    if (weekIdForTransferData) {
      const transferDataResult = await api.get(`/api/transfer/v2/transferData/${weekIdForTransferData}`)
      transferData.value = transferDataResult?.mergedData?.find(
          transfer => transfer.transferId === route.params.transferId
      ) || null
    }

    await refreshData();
    refreshDataHook();
  } catch (err) {
    console.warn('Error loading transfer data:', err)
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {

  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
});

</script>
